import { useNavigate, useSearchParams } from "#routers/hooks";
import { CustomDashboardTab } from "#src/batteries-included-components/Tabs/CustomDashboard/CustomDashboardTab";
import { InsightsDashboardTab } from "#src/batteries-included-components/Tabs/Insights/InsightsDashboardTab";
import {
  useAuthenticatedContext,
  useIsFeatureAvailable,
  useLegacyIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { Button, Page, Tab } from "@validereinc/common-components";
import { FeatureFlags, PermissionsMap } from "@validereinc/domain-controllers";
import React from "react";
import { DashboardsRoutePath, ManageInsightsRoutePath } from ".";

export const DashboardPage = () => {
  const {
    v2: {
      userInfo: { user },
    },
  } = useAuthenticatedContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isCustomTabEnabledLegacy] = useLegacyIsFeatureAvailable({
    featureFlagQuery: [FeatureFlags.LegacyDashboards],
    permissionQuery: [PermissionsMap.LegacyDashboards],
    featureId: "dashboards",
  });
  const [isCustomTabEnabled] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.Dashboards,
    permissionQuery: PermissionsMap.DashboardsRead,
  });
  const [isInsightsTabEnabledLegacy] = useLegacyIsFeatureAvailable({
    featureFlagQuery: [FeatureFlags.LegacyInsightsDashboard],
    permissionQuery: [PermissionsMap.LegacyInsights],
    featureId: "dashboards",
  });
  const [isInsightsTabEnabled] = useIsFeatureAvailable({
    featureFlagQuery: FeatureFlags.DashboardsInsights,
    permissionQuery: PermissionsMap.InsightsDashboardsRead,
  });
  const [isManageInsightsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: {
      $and: [FeatureFlags.DashboardsManageInsights],
    },
    permissionQuery: PermissionsMap.InsightsDashboardsWrite,
  });
  const [breadcrumbs] = useBreadcrumbsFromRoute(DashboardsRoutePath);

  const handleActiveTabKeyChange = (tabKey) => {
    setSearchParams({ tab: tabKey });
  };

  const getActiveTabKey = () => {
    if (searchParams?.tab) {
      return searchParams?.tab;
    }

    if (isInsightsTabEnabled) {
      return "insights";
    }

    if (isCustomTabEnabled) {
      return "custom";
    }

    return "";
  };

  const actionRow = [
    ...(getActiveTabKey() === "insights" &&
    (isManageInsightsAvailable || isInsightsTabEnabledLegacy) &&
    user?.can_access_quicksight_console
      ? [
          <Button
            key="manage-insights"
            variant="primary"
            onClick={() => navigate({ pathname: ManageInsightsRoutePath.path })}
          >
            Manage Insights
          </Button>,
        ]
      : []),
  ];

  const shouldShowInsightsTab =
    (isInsightsTabEnabledLegacy &&
      (user?.can_access_quicksight_dashboards || user?.quicksight)) ||
    (isInsightsTabEnabled && user?.can_access_quicksight_dashboards);

  return (
    <Page
      title="Dashboards"
      breadcrumbs={breadcrumbs}
      activeTabKey={getActiveTabKey()}
      onActiveTabKeyChange={handleActiveTabKeyChange}
      actionRow={actionRow}
    >
      {shouldShowInsightsTab ? (
        <Tab
          tabKey="insights"
          title="Insights"
        >
          <InsightsDashboardTab />
        </Tab>
      ) : null}

      {isCustomTabEnabled || isCustomTabEnabledLegacy ? (
        <Tab
          tabKey="custom"
          title="Custom Dashboards"
        >
          <CustomDashboardTab />
        </Tab>
      ) : null}
    </Page>
  );
};
